import * as React from 'react';
import { styled, alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import AppBar from '@mui/material/AppBar';
import { Button } from '@mui/material';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Container from '@mui/material/Container';
import Drawer from '@mui/material/Drawer';
import MenuIcon from '@mui/icons-material/Menu';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ThemeIconDropdown from '../theme/ThemeIconDropdown';
import { Handshake, HomeIcon } from 'lucide-react';
import { ContactMail, GroupAdd, Quiz, SettingsSuggest } from '@mui/icons-material';
import Logo from '../logo.svg';
import { useNavigate, useLocation } from 'react-router-dom';

const StyledToolbar = styled(Toolbar, {
  shouldForwardProp: (prop) => prop !== 'scrolled',
})(({ theme, scrolled }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexShrink: 0,
  borderRadius: `calc(${theme.shape.borderRadius}px + 8px)`,
  backdropFilter: 'blur(24px)',
  border: '1px solid',
  borderColor: (theme.vars || theme).palette.divider,
  backgroundColor: theme.vars
    ? `rgba(${theme.vars.palette.background.defaultChannel} / ${scrolled ? 0.75 : 0.4})`
    : alpha(theme.palette.background.default, scrolled ? 0.75 : 0.4),
  boxShadow: scrolled
    ? '0 8px 30px hsla(220,40%,10%,0.18)'
    : (theme.vars || theme).shadows[1],
  padding: scrolled ? '4px 12px' : '8px 12px',
  transition: 'padding 0.3s ease, background-color 0.3s ease, box-shadow 0.3s ease',
}));

export default function MenuBar({
  scrollToSection,
  highlightsRef,
  servicesRef,
  whyChooseUsRef,
  faqsRef,
  contactUsRef,
}) {
  const [open, setOpen] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);

  const toggleDrawer = (newOpen) => () => {
    setOpen(newOpen);
  };

  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const navigate = useNavigate();
  const location = useLocation();

  const goHome = () => {
    if (location.pathname !== '/') {
      // Navigate to the home route, then jump to the top.
      navigate('/');
      window.scrollTo({ top: 0, left: 0 });
    } else {
      // Already on the home page — smooth-scroll back to the top.
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <AppBar
      position="fixed"
      enableColorOnDark
      sx={{
        boxShadow: 0,
        bgcolor: 'transparent',
        backgroundImage: 'none',
        mt: 'calc(var(--template-frame-height, 0px) + 28px)',
      }}
    >
      <Container maxWidth="lg">
        <StyledToolbar variant="dense" disableGutters scrolled={scrolled}>
          <Box
            onClick={goHome}
            sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', pl: 0.5 }}
          >
            <img
              src={Logo}
              alt="FalconXcellence logo"
              style={{ height: 30, width: 30, objectFit: 'contain', borderRadius: 8 }}
            />
            <Box
              component="span"
              sx={{
                display: { xs: 'none', sm: 'block' },
                fontFamily: '"Sora","Inter",sans-serif',
                fontWeight: 800,
                fontSize: '1.05rem',
                letterSpacing: -0.3,
              }}
            >
              Falcon<Box component="span" className="fx-gradient-text">Xcellence</Box>
            </Box>
          </Box>
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', px: 0 }}>
            <Box sx={{ display: { xs: 'none', md: 'flex' }, ml: 2 }}>
              <Button
                variant="text"
                color="info"
                size="small"
                startIcon={<HomeIcon />}
                onClick={goHome}
              >
                Home
              </Button>
              <Button
                variant="text"
                color="info"
                size="small"
                startIcon={<SettingsSuggest />}
                onClick={() => scrollToSection(servicesRef)}
              >
                Services
              </Button>
              <Button
                variant="text"
                color="info"
                size="small"
                startIcon={<Handshake />}
                onClick={() => scrollToSection(whyChooseUsRef)}
              >
                Why Choose Us
              </Button>
              <Button
                variant="text"
                color="info"
                size="small"
                startIcon={<GroupAdd />}
                onClick={() => navigate('/careers')}
              >
                Careers
              </Button>
              <Button
                variant="text"
                color="info"
                size="small"
                sx={{ minWidth: 0 }}
                startIcon={<Quiz />}
                onClick={() => scrollToSection(faqsRef)}
              >
                FAQ
              </Button>
              <Button
                variant="text"
                color="info"
                size="small"
                sx={{ minWidth: 0 }}
                startIcon={<ContactMail />}
                onClick={() => scrollToSection(contactUsRef, false)}
              >
                Contact Us
              </Button>
            </Box>
          </Box>
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              gap: 1.5,
              alignItems: 'center',
            }}
          >
            <ThemeIconDropdown />
            <Button
              variant="contained"
              size="small"
              onClick={() => scrollToSection(contactUsRef, false)}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: 2,
                px: 2,
                color: '#fff !important',
                border: 'none !important',
                backgroundColor: 'hsl(230,90%,55%) !important',
                backgroundImage:
                  'linear-gradient(100deg, hsl(210,98%,52%), hsl(265,90%,60%)) !important',
                boxShadow: '0 6px 18px hsla(220,90%,50%,0.35) !important',
                '&:hover': {
                  backgroundImage:
                    'linear-gradient(100deg, hsl(210,98%,58%), hsl(265,90%,66%)) !important',
                },
              }}
            >
              Let's talk
            </Button>
          </Box>
          <Box sx={{ display: { xs: 'flex', md: 'none' }, gap: 1 }}>
            <ThemeIconDropdown size="medium" />
            <IconButton aria-label="Menu button" onClick={toggleDrawer(true)}>
              <MenuIcon />
            </IconButton>
            <Drawer
              anchor="top"
              open={open}
              onClose={toggleDrawer(false)}
              PaperProps={{
                sx: {
                  top: 'var(--template-frame-height, 0px)',
                },
              }}
            >
              <Box sx={{ p: 2, backgroundColor: 'background.default' }}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <IconButton onClick={toggleDrawer(false)}>
                    <CloseRoundedIcon />
                  </IconButton>
                </Box>
                <Box
                  sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}
                >
                  <Button
                    variant="text"
                    color="info"
                    onClick={() => {
                      goHome();
                      setOpen(false);
                    }}
                  >
                    Home
                  </Button>
                  <Button
                    variant="text"
                    color="info"
                    onClick={() => {
                      scrollToSection(servicesRef);
                      setOpen(false);
                    }}
                  >
                    Services
                  </Button>
                  <Button
                    variant="text"
                    color="info"
                    onClick={() => {
                      scrollToSection(whyChooseUsRef);
                      setOpen(false);
                    }}
                  >
                    Why Choose Us
                  </Button>
                  <Button
                    variant="text"
                    color="info"
                    onClick={() => {
                      navigate('/careers');
                      setOpen(false);
                    }}
                  >
                    Careers
                  </Button>
                  <Button
                    variant="text"
                    color="info"
                    onClick={() => {
                      scrollToSection(faqsRef);
                      setOpen(false);
                    }}
                  >
                    FAQ
                  </Button>
                  <Button
                    variant="text"
                    color="info"
                    onClick={() => {
                      scrollToSection(contactUsRef, false);
                      setOpen(false);
                    }}
                  >
                    Contact Us
                  </Button>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => {
                      scrollToSection(contactUsRef, false);
                      setOpen(false);
                    }}
                    sx={{
                      mt: 1,
                      textTransform: 'none',
                      fontWeight: 700,
                      borderRadius: 2,
                      color: '#fff !important',
                      border: 'none !important',
                      backgroundColor: 'hsl(230,90%,55%) !important',
                      backgroundImage:
                        'linear-gradient(100deg, hsl(210,98%,52%), hsl(265,90%,60%)) !important',
                    }}
                  >
                    Let's talk
                  </Button>
                </Box>
              </Box>
            </Drawer>
          </Box>
        </StyledToolbar>
      </Container>
    </AppBar>
  );
}
